/**
 * DOM utility helpers.
 */

/**
 * Type-safe closest() that works on any EventTarget.
 *
 * ⚠️ **Do not narrow this to `HTMLElement`.** It used to read
 * `if (!(target instanceof HTMLElement)) return null` — and that silently killed
 * every module that routes clicks through here (click-action, event-tracker,
 * form-handler, form-lifecycle) on figma-exported pages: their CTAs are inline
 * `<svg>`, so a click lands on `<path>`, whose type is `SVGElement` — **not** an
 * `HTMLElement`. Symptom: the action does nothing and the click is never tracked,
 * with no error anywhere. `Element.closest()` is defined for SVG too, so the only
 * thing we need is "does this target have closest()".
 *
 * Duck-typing rather than `instanceof Element` on purpose: `instanceof` also fails
 * across realms (an element from another iframe/window has a different prototype).
 */
export function closestFrom(target: EventTarget | null, selector: string): Element | null {
  const el = target as Element | null;
  if (!el || typeof el.closest !== 'function') return null;
  return el.closest(selector);
}

/**
 * Tags whose bare `href` belongs to the browser, not the SDK (`<a>`, `<area>`).
 * Case-insensitive on purpose: SVG anchors report a lowercase `tagName` ('a'),
 * and figma-exported pages are full of inline SVG.
 */
export function isNativeLinkTag(el: Element): boolean {
  return /^a(rea)?$/i.test(el.tagName);
}

const HTML_NS = 'http://www.w3.org/1999/xhtml';

/**
 * Does the platform itself give this element's `href` a meaning?
 *
 * The legacy contract could squat on `href` exactly where the platform does **not**
 * define it (div / span / button / img / h1 …), so the question is not "which tags
 * are links" — it is "does the DOM expose an href property here":
 *   - `string`            → HTML hyperlink (`<a>` / `<area>` / `<link>` / `<base>`)
 *   - `SVGAnimatedString` → SVG structural reference (`<use href="#icon">` points at
 *     a symbol, `<image href>` at a bitmap, plus textPath / pattern / gradients …)
 *   - `undefined`         → nobody owns it, so a bare href there is our legacy action
 *
 * Asking the platform instead of listing tags is deliberate: `closestFrom` now sees
 * every element type (SVG included), so a click can land on things no list of ours
 * would have predicted. Treating `<use href="#icon">` as an action navigates the page
 * to that hash — an icon that silently scrolls the page away.
 *
 * The namespace clause is **not** redundant with the property check: jsdom implements
 * no SVG element interfaces (every SVG child is a bare `SVGElement` with no `href`
 * property), so without it this guard would be untestable here and would rot. Foreign
 * content never hosts an editor click action anyway — except the `<svg>` root, which
 * has no href semantics of its own and *is* the CTA host on figma-exported pages.
 */
function hasNativeHrefSemantics(el: Element): boolean {
  if (typeof (el as unknown as { href?: unknown }).href !== 'undefined') return true;
  return el.namespaceURI !== HTML_NS && el.localName !== 'svg';
}

/**
 * Read the click-action href of an element.
 *
 * `data-pt-href` (current editor contract, valid on any tag — including `<a>`,
 * where it carries non-navigation actions like `form:`) wins. A bare `href`
 * counts only where the platform has no use for it (see hasNativeHrefSemantics):
 * that is the legacy contract for pages published before the data-pt-href
 * migration. A plain `<a href>` returns null — native.
 */
export function getActionHref(el: Element): string | null {
  const managed = el.getAttribute('data-pt-href');
  if (managed !== null) return managed;
  return hasNativeHrefSemantics(el) ? null : el.getAttribute('href');
}

/** Companion of getActionHref for the open-target (`_blank`), same precedence. */
export function getActionTarget(el: Element): string | null {
  const managed = el.getAttribute('data-pt-target');
  if (managed !== null) return managed;
  return hasNativeHrefSemantics(el) ? null : el.getAttribute('target');
}

/** Marker on the hidden SEO anchors the publish pipeline generates. */
const GEN_LINK_SELECTOR = '[data-pt-gen="link"]';

/**
 * Element text with the publish-generated hidden SEO anchors excluded.
 *
 * The publish pipeline (iam `rewriteClickActions`) appends
 * `<a class="pt-gen-link" data-pt-gen="link">` inside every non-`<a>` click-action
 * host, so crawlers and keyboard users get a real link. **That anchor's text is not
 * host content**, and reading it back through `textContent` corrupted every reported
 * `label`:
 *   - host WITH copy   → anchor text = the same copy → "Buy nowBuy now"
 *   - host WITHOUT copy (figma SVG button) → anchor text falls back to the href
 *     → the label became a raw URL, truncated at 50 chars
 * Both were silent: the event fired, the name was right, only the label was junk.
 *
 * Fast path: hosts with no generated anchor — the vast majority, and *every* page
 * published before the migration — return `textContent` untouched, so nothing about
 * legacy pages changes.
 */
export function visibleText(el: Element): string {
  if (typeof el.querySelector !== 'function' || !el.querySelector(GEN_LINK_SELECTOR)) {
    return el.textContent || '';
  }
  let out = '';
  // Manual walk rather than cloneNode+remove: hosts can wrap large inline <svg>
  // trees (figma exports), and this runs on every tracked click.
  const walk = (node: Node): void => {
    for (let c: Node | null = node.firstChild; c; c = c.nextSibling) {
      if (c.nodeType === 3) {
        out += c.nodeValue || '';
        continue;
      }
      if (c.nodeType !== 1) continue;
      const child = c as Element;
      if (child.getAttribute && child.getAttribute('data-pt-gen') === 'link') continue;
      walk(child);
    }
  };
  walk(el);
  return out;
}

/** Get trimmed text content, capped at maxLen (generated SEO anchors excluded) */
export function getTextContent(el: Element, maxLen = 50): string {
  return visibleText(el).trim().slice(0, maxLen);
}

/** Inject a <style> tag into <head> */
export function injectStyle(css: string): HTMLStyleElement {
  const style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);
  return style;
}

/** Escape a string for use inside a CSS attribute selector value. */
export function cssAttrEscape(value: string): string {
  return value.replace(/["\\]/g, '\\$&');
}

/** Look up a form wrapper by its public name (data-form-name) or, fallback, by data-pt-id. */
export function findFormWrapperByName(name: string): HTMLElement | null {
  const safe = cssAttrEscape(name);
  return (
    document.querySelector<HTMLElement>(`[data-form-name="${safe}"]`) ||
    document.querySelector<HTMLElement>(`[data-pt-id="${safe}"]`)
  );
}
